(() => {
  const handoffKey = 'bt:collection-return:handoff';
  const stateKey = 'btCollectionReturn';
  const maxDepth = 16;
  const maxAge = 30 * 60 * 1000;
  const handoffAge = 2 * 60 * 1000;
  const now = Date.now();

  const parse = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin && !url.username && !url.password ? url : null;
    } catch (_) { return null; }
  };
  const route = (url, kind) => url?.pathname.match(new RegExp(
    `^(/(?:[a-z]{2}(?:-[a-z]{2})?))?/${kind}/([a-z0-9][a-z0-9-]*)/?$`, 'i'
  ));
  const locale = (url) => url?.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i)?.[1].toLowerCase() || '';
  const results = (url) => {
    const search = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search\/?$/i.test(url?.pathname || '');
    if (!route(url, 'collections') && !search) return null;
    if (search && !url.searchParams.get('q')?.trim()) return null;
    const destination = new URL(url.pathname, window.location.origin);
    for (const [key, value] of url.searchParams) {
      if (key.startsWith('filter.') || ['sort_by', 'page'].includes(key) ||
          (search && ['q', 'type', 'options[prefix]', 'options[unavailable_products]'].includes(key))) {
        destination.searchParams.append(key, value);
      }
    }
    return destination.pathname + destination.search;
  };
  const current = parse(window.location.href);
  if (!route(current, 'products') && !results(current)) return;
  const market = document.querySelector('script[data-bt-selection-context]')?.dataset;
  const validMarket = (value) => /^[A-Z]{3}$/.test(value?.currency || '') && /^[A-Z]{2}$/.test(value?.country || '');
  const source = document.referrer ? parse(document.referrer) : null;

  // Only one outgoing product click is retained, briefly. No raw referrer,
  // product query string, cookie or list of visited products is stored.
  const clearHandoff = () => {
    try { window.sessionStorage.removeItem(handoffKey); } catch (_) { /* Optional storage. */ }
  };
  let pending;
  try { pending = JSON.parse(window.sessionStorage.getItem(handoffKey)); } catch (_) { /* Optional storage. */ }
  clearHandoff();

  const valid = (context) => {
    if (!context || context.version !== 1 || context.product !== current.pathname ||
        Object.keys(context).some((key) => !['version', 'href', 'product', 'created', 'depth', 'anchor', 'currency', 'country', 'origin'].includes(key)) ||
        ('origin' in context && (context.origin !== 'predictive-search' || context.anchor !== null)) ||
        (('currency' in context || 'country' in context) && !validMarket(context)) ||
        !Number.isInteger(context.depth) || context.depth < 1 || context.depth > maxDepth ||
        !Number.isFinite(context.created) || context.created > now || now - context.created > maxAge ||
        typeof context.href !== 'string' || !context.href.startsWith('/') || context.href.startsWith('//') ||
        (context.anchor !== null && (typeof context.anchor !== 'string' || context.anchor.length > 100))) return false;
    const destination = parse(context.href);
    return destination && results(destination) === context.href && locale(destination) === locale(current) &&
      (!context.origin || (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search\/?$/i.test(destination.pathname) &&
        ![...destination.searchParams.keys()].some(key => key.startsWith('filter.') || ['sort_by', 'page'].includes(key))));
  };
  const entries = () => {
    try {
      const navigation = window.navigation;
      const index = navigation?.currentEntry?.index;
      if (!Number.isInteger(index) || index < 0 || typeof navigation.entries !== 'function') return null;
      const items = navigation.entries();
      const entry = items.find((item) => item.index === index);
      if (!entry || entry.key !== navigation.currentEntry.key || parse(entry.url)?.pathname !== current.pathname) return null;
      return { index, items };
    } catch (_) { return null; }
  };
  const supportsEntries = () => {
    try { return typeof window.navigation?.entries === 'function'; }
    catch (_) { return true; /* An inaccessible API is not proof of an immediate previous page. */ }
  };
  const historyDepth = (context) => {
    const snapshot = entries();
    if (!snapshot || !context.anchor) return null;
    const anchor = snapshot.items.find((entry) => entry.key === context.anchor);
    const depth = snapshot.index - (anchor?.index ?? snapshot.index);
    if (depth !== context.depth || depth < 1 || depth > maxDepth ||
        results(parse(anchor.url)) !== context.href) return null;
    // Never skip an intervening account, checkout, external or other-locale page.
    for (let index = anchor.index + 1; index <= snapshot.index; index++) {
      const url = parse(snapshot.items.find((entry) => entry.index === index)?.url);
      if (!route(url, 'products') || locale(url) !== locale(current)) return null;
    }
    return depth;
  };

  // Stamp the market at the results page, before leaving it. A price selected
  // in one country/currency must never be interpreted in another market.
  if (results(current)) {
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey ||
          event.shiftKey || event.altKey) return;
      const link = event.target?.closest?.('a[href]');
      if (!link || link.hasAttribute('download')) return;
      if (link.closest?.('predictive-search')) return;
      const target = link.target || document.querySelector('base[target]')?.target;
      if (target && target !== '_self') return;
      clearHandoff();
      const chosen = parse(window.location.href);
      const href = results(chosen);
      const destination = parse(link.href);
      if (!href || !route(destination, 'products') || locale(destination) !== locale(chosen) || !validMarket(market)) return;
      const snapshot = entries();
      const anchor = snapshot?.items.find((entry) => entry.index === snapshot.index)?.key || null;
      try {
        const written = Date.now();
        window.sessionStorage.setItem(handoffKey, JSON.stringify({
          version: 1, from: chosen.pathname, to: destination.pathname, written,
          context: { version: 1, href, product: destination.pathname, created: written,
            depth: 1, anchor, currency: market.currency, country: market.country },
        }));
      } catch (_) { /* Results and product links never depend on storage. */ }
    });
    return;
  }

  let context;
  try {
    const type = window.performance?.getEntriesByType('navigation')[0]?.type;
    const saved = window.history?.state?.[stateKey];
    if (['reload', 'back_forward'].includes(type) && valid(saved) && source &&
        locale(source) === locale(current) && (results(source) === saved.href || route(source, 'products') || saved.origin === 'predictive-search')) {
      context = saved;
    }
  } catch (_) { /* A normal link remains available without history state. */ }

  if (!context && source && locale(source) === locale(current)) {
    const href = results(source);
    if (pending?.version === 1 && pending.context?.origin === 'predictive-search' &&
        pending.from === source.pathname && pending.to === current.pathname &&
        Number.isFinite(pending.written) && pending.written <= now && now - pending.written <= handoffAge &&
        valid(pending.context) && pending.context.depth === 1) {
      context = pending.context;
    } else if (href) {
      const snapshot = entries();
      const previous = snapshot?.items.find((entry) => entry.index === snapshot.index - 1);
      const anchor = previous && results(parse(previous.url)) === href ? previous.key : null;
      if (pending?.version === 1 && pending.from === source.pathname && pending.to === current.pathname &&
          Number.isFinite(pending.written) && pending.written <= now && now - pending.written <= handoffAge &&
          valid(pending.context) && pending.context.href === href && pending.context.depth === 1) {
        context = { ...pending.context, anchor };
      } else context = { version: 1, href, product: current.pathname, created: now, depth: 1, anchor };
    } else if (route(source, 'products') && pending?.version === 1 &&
        pending.from === source.pathname && pending.to === current.pathname &&
        Number.isFinite(pending.written) && pending.written <= now && now - pending.written <= handoffAge &&
        valid(pending.context)) {
      context = pending.context;
    }
  }
  if (!context) return;

  const ageKey = (value) => {
    // Same canonical grouping as the age facets and brincatoys-valid-age.liquid.
    const label = String(value).trim().toLowerCase();
    const parts = label.replace(/-/g, ' ').replace(/\+/g, '').trim().split(/\s+/);
    const amount = Number(parts[0]);
    if (!Number.isFinite(amount) || String(amount) !== parts[0]) return '';
    const recognised = (parts.length === 2 && (label.includes('+') || parts[1] === 'plus')) ||
      (parts.length === 3 && parts[2] === 'plus') ||
      (parts.length === 3 && parts[1] === 'plus' && !label.startsWith('-')) ||
      (parts.length === 4 && parts[2] === 'and' && ['up', 'over'].includes(parts[3]));
    if (!recognised) return '';
    const unit = parts.length === 3 && parts[1] === 'plus' ? parts[2] : parts[1];
    if (['mês', 'meses', 'mes', 'month', 'months'].includes(unit)) return `+ ${amount} ${amount === 1 ? 'mês' : 'meses'}`;
    if (['ano', 'anos', 'año', 'años', 'year', 'years'].includes(unit) || (parts.length === 2 && parts[1] === 'plus')) {
      return amount >= 14 ? '__adults__' : `+ ${amount} ${amount === 1 ? 'ano' : 'anos'}`;
    }
    return '';
  };
  const priceBound = (params, key) => {
    const values = params.getAll(key);
    if (!values.length || (values.length === 1 && !values[0].trim())) return null;
    if (values.length !== 1 || !/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(values[0])) return NaN;
    const [whole, fraction = ''] = values[0].split('.');
    const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
    return Number.isSafeInteger(cents) ? cents : NaN;
  };
  window.BtProductSelectionContext = {
    get() {
      if (!validMarket(market) || context.currency !== market.currency || context.country !== market.country ||
          Date.now() - context.created > maxAge) return null;
      const params = parse(context.href).searchParams;
      const ages = params.getAll('filter.p.product_type').map(ageKey);
      const vendors = [...new Set(params.getAll('filter.p.vendor').filter(value => value.trim()))];
      const minPrice = priceBound(params, 'filter.v.price.gte');
      const maxPrice = priceBound(params, 'filter.v.price.lte');
      if (ages.some(age => !age) || Number.isNaN(minPrice) || Number.isNaN(maxPrice) ||
          (minPrice !== null && maxPrice !== null && minPrice > maxPrice)) return null;
      return { ages: [...new Set(ages)], minPrice, maxPrice, currency: context.currency, country: context.country,
        ...(vendors.length ? { vendors } : {}) };
    },
  };

  try {
    const state = window.history?.state;
    if (state == null || Object.prototype.toString.call(state) === '[object Object]') {
      window.history.replaceState({ ...state, [stateKey]: context }, '');
    }
  } catch (_) { /* Do not replace an unrelated or unavailable history state. */ }

  document.querySelectorAll('[data-bt-collection-return]').forEach((item) => {
    const link = item.querySelector('[data-bt-collection-return-link]');
    if (!link) return;
    link.href = context.href;
    item.hidden = false;
    item.classList.add('flex', 'lh-15');
    link.addEventListener('click', (event) => {
      const target = link.target || document.querySelector('base[target]')?.target;
      if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey ||
          event.shiftKey || event.altKey || (target && target !== '_self')) return;
      const depth = historyDepth(context);
      const history = window.history;
      // Older browsers still resume an immediately preceding results page.
      // Multiple products need the actual results entry, not history.length guesses.
      const immediate = !supportsEntries() && context.depth === 1 && results(source) === context.href &&
        history?.length > 1;
      try {
        if (depth > 0 && typeof window.navigation?.traverseTo === 'function') {
          // A frame can add joint history entries without changing the top-level
          // index. Traverse to the proven entry key, not an assumed history.go delta.
          const currentKey = window.navigation.currentEntry.key;
          const traversal = window.navigation.traverseTo(context.anchor);
          traversal.committed.catch((error) => {
            if (error.name !== 'AbortError' && window.navigation.currentEntry.key === currentKey) {
              window.location.assign(context.href);
            }
          });
          traversal.finished.catch(() => {});
        }
        else if ((depth === 1 || immediate) && typeof history?.back === 'function') history.back();
        else return;
        event.preventDefault();
      } catch (_) { /* Leave the sanitized href available if history cannot be used. */ }
    });
  });

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey ||
        event.shiftKey || event.altKey) return;
    const link = event.target?.closest?.('a[href]');
    if (!link || link.hasAttribute('download')) return;
    if (link.closest?.('predictive-search')) return;
    const target = link.target || document.querySelector('base[target]')?.target;
    if (target && target !== '_self') return;
    clearHandoff();
    const destination = parse(link.href);
    if (!route(destination, 'products') || destination.pathname === current.pathname ||
        locale(destination) !== locale(current) || context.depth >= maxDepth) return;
    try {
      window.sessionStorage.setItem(handoffKey, JSON.stringify({
        version: 1, from: current.pathname, to: destination.pathname, written: Date.now(),
        context: { ...context, product: destination.pathname, depth: context.depth + 1 },
      }));
    } catch (_) { /* Product navigation itself never depends on storage. */ }
  });
})();
